import { useMemo } from 'react';

import { useContentfulContentTypeEntry } from '@order/Contentful';

import type { Asset } from 'contentful';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns global Contentful settings and assets for the Order app, such as
 * seasonal brand accents shared across multiple screens.
 */
export const useGlobalContent = (params: Params = {}) => {
  const { pause } = params;

  const contentfulResponse = useContentfulContentTypeEntry<GlobalContentFields>({
    contentType: GLOBAL_CONTENT_TYPE,
    pause,
  });

  // ─── Derived ─────────────────────────────────────────────────────────────

  const mappedFields = useMemo<MappedGlobalFields>(
    () => ({
      brandIcon: contentfulResponse.data?.fields?.brandIcon,
      noticeText: contentfulResponse.data?.fields?.globalNoticeText,
      noticePalette: contentfulResponse.data?.fields?.globalNoticePalette,
    }),
    [contentfulResponse.data],
  );

  return {
    fields: mappedFields,
    fetching: contentfulResponse.fetching,
  };
};

// ─── Constants ───────────────────────────────────────────────────────────────

const GLOBAL_CONTENT_TYPE = 'global';

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  pause?: boolean;
};

type GlobalContentFields = {
  brandIcon?: Asset;
  globalNoticeText?: string;
  globalNoticePalette?: string;
};

type MappedGlobalFields = {
  brandIcon: Asset | undefined;
  noticeText: string | undefined;
  noticePalette: string | undefined;
};
